import type { CompanyRegistrationValues } from "../model/registrationTypes";
import { InvoiceTarget } from "../model/registrationEnums";
import { emptyEmployee } from "../model/registrationInitialValues";

export type SetFieldValue = (
  field: string,
  value: unknown,
  shouldValidate?: boolean,
) => void | Promise<unknown>;

export type EmployeeBase =
  "enkeltmandsvirksomhed" | "interessentskab" | "advokatselskab";

export const hasEmployeeValues = (
  employeeList: CompanyRegistrationValues[EmployeeBase]["employeeList"],
): boolean =>
  employeeList.length !== 1 ||
  employeeList.some(
    (employee) =>
      employee.employeeName ||
      employee.employeeRole ||
      employee.employeeEmail ||
      employee.employeePhone,
  );

export const clearTextFieldIfSet = (
  setFieldValue: SetFieldValue,
  name: string,
  value: string,
): void => {
  if (value) {
    void setFieldValue(name, "", false);
  }
};

export const clearTextFieldsIfSet = (
  setFieldValue: SetFieldValue,
  fields: Array<[string, string]>,
): void => {
  fields.forEach(([name, value]) =>
    clearTextFieldIfSet(setFieldValue, name, value),
  );
};

export const resetEmployeesIfNeeded = (
  setFieldValue: SetFieldValue,
  base: EmployeeBase,
  employeeList: CompanyRegistrationValues[EmployeeBase]["employeeList"],
): void => {
  if (hasEmployeeValues(employeeList)) {
    void setFieldValue(`${base}.employeeList`, [emptyEmployee()], false);
  }
};

/**
 * Only one of the two invoice details belongs to the chosen dispatch channel —
 * drop the other one so it does not travel along in the submitted payload.
 */
export const clearInvoiceDetailsNotMatchingTarget = (
  setFieldValue: SetFieldValue,
  base: string,
  invoiceTarget: string,
  invoiceEmail: string,
  eanNumber: string,
): void => {
  if (invoiceTarget !== InvoiceTarget.Mail) {
    clearTextFieldIfSet(setFieldValue, `${base}.invoiceEmail`, invoiceEmail);
  }

  if (invoiceTarget !== InvoiceTarget.EAN) {
    clearTextFieldIfSet(setFieldValue, `${base}.eanNumber`, eanNumber);
  }
};

export const syncArrayFieldIfChanged = <T>(
  setFieldValue: SetFieldValue,
  fieldName: string,
  current: T[],
  synced: T[],
  hasChanged: (currentRow: T, syncedRow: T) => boolean,
): void => {
  if (synced.some((row, index) => hasChanged(current[index], row))) {
    void setFieldValue(fieldName, synced, false);
  }
};
